import React, { useState, useRef } from 'react';
import { Box, ImageList, ImageListItem, ImageListItemBar, SxProps, Theme } from '@mui/material';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DragHandle } from '@mui/icons-material';

interface ImageItem {
	imgUrl: string;
	title: string;
	id: number;
	rank?: number;
}

interface Props {
	images: ImageItem[];
	onDeleteImageClick?: (imageId: number) => void;
	onReorderSubmit?: (reorderedItems: {id: number, rank: number}[]) => void;
	onItemClick?: (image: ImageItem, index: number) => void;
	sx?: SxProps<Theme>;
}

export const SimpleImageList = ({images, onDeleteImageClick, onReorderSubmit, onItemClick, sx}: Props) => {
	const isEditable = Boolean(onDeleteImageClick);
	const isReorderable = Boolean(onReorderSubmit);
	const isClickable = Boolean(onItemClick);
	const [draggedItem, setDraggedItem] = useState<ImageItem | null>(null);
	const [dragOverItem, setDragOverItem] = useState<ImageItem | null>(null);
	const dragRef = useRef<HTMLDivElement>(null);

	const handleDragStart = (e: React.DragEvent, item: ImageItem) => {
		setDraggedItem(item);
		e.dataTransfer.effectAllowed = 'move';
		if (dragRef.current) {
			e.dataTransfer.setDragImage(dragRef.current, 0, 0);
		}
	};

	const handleDragOver = (e: React.DragEvent, item: ImageItem) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		if (draggedItem && draggedItem.id !== item.id) {
			setDragOverItem(item);
		}
	};

	const handleDragLeave = () => {
		setDragOverItem(null);
	};

	const handleDrop = (e: React.DragEvent, targetItem: ImageItem) => {
		e.preventDefault();
		if (!draggedItem || draggedItem.id === targetItem.id) {
			setDraggedItem(null);
			setDragOverItem(null);
			return;
		}

		// Create new array with reordered items
		const reorderedImages = [...images];
		const draggedIndex = reorderedImages.findIndex(img => img.id === draggedItem.id);
		const targetIndex = reorderedImages.findIndex(img => img.id === targetItem.id);
		
		// Remove dragged item from its current position
		const [removed] = reorderedImages.splice(draggedIndex, 1);
		
		// Insert at target position
		reorderedImages.splice(targetIndex, 0, removed);

		// Update ranks based on new positions
		const reorderedItems = reorderedImages.map((img, index) => ({
			id: img.id,
			rank: index + 1
		}));

		// Call the callback with reordered items
		onReorderSubmit?.(reorderedItems);

		setDraggedItem(null);
		setDragOverItem(null);
	};

	const handleDragEnd = () => {
		setDraggedItem(null);
		setDragOverItem(null);
	};

	return (
		<Box sx={{...sx}}>
			{/* Hidden drag image element */}
			<div
				ref={dragRef}
				style={{
					position: 'absolute',
					top: '-1000px',
					left: '-1000px',
					width: '100px',
					height: '100px',
					backgroundColor: 'rgba(0,0,0,0.5)',
					borderRadius: '8px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: 'white',
					fontSize: '12px'
				}}
			>
				Moving...
			</div>
			
			<ImageList
				variant="quilted"
				cols={4}
				rowHeight={100}
				sx={{m:0}}
			>
				{images.map((image, index) => {
					const colsRows = index === 0 ? 4 : 2;
					const isDragging = draggedItem?.id === image.id;
					const isDragOver = dragOverItem?.id === image.id;
					
					return (
						<ImageListItem 
							key={image.imgUrl} 
							cols={colsRows} 
							rows={colsRows} 
							sx={{
								borderRadius: 2, 
								overflow: 'hidden', 
								boxShadow: 3,
								opacity: isDragging ? 0.5 : 1,
								transform: isDragging ? 'scale(0.95)' : 'scale(1)',
								transition: 'all 0.2s ease',
								border: isDragOver ? '2px dashed #1976d2' : 'none',
								backgroundColor: isDragOver ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
								cursor: isClickable ? 'pointer' : 'default',
							}}
							onClick={isClickable ? () => onItemClick?.(image, index) : undefined}
							onDragOver={isReorderable ? (e) => handleDragOver(e, image) : undefined}
							onDragLeave={isReorderable ? handleDragLeave : undefined}
							onDrop={isReorderable ? (e) => handleDrop(e, image) : undefined}
							onDragEnd={isReorderable ? handleDragEnd : undefined}
						>
							<img
								src={image.imgUrl}
								alt={image.title}
								loading="lazy"
								style={{overflow:'hidden', userSelect: 'none'}}
							/>
							{(isEditable || isReorderable) && <ImageListItemBar
								actionIcon={<Box sx={{display: 'flex', gap: 1}}>
									{isReorderable && (
										<IconButton
											sx={{ 
												color: 'rgba(255, 255, 255, 0.54)',
												cursor: 'grab',
												'&:active': {
													cursor: 'grabbing'
												}
											}}
											aria-label="Drag to reorder"
											draggable={true}
											onDragStart={(e) => handleDragStart(e, image)}
										>
											<DragHandle />
										</IconButton>
									)}
									{isEditable && (
										<IconButton
											sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
											aria-label={`Delete ${image.title}`}
											onClick={() => onDeleteImageClick?.(image.id)}
										>
											<DeleteIcon />
										</IconButton>
									)}
								</Box>}
							/>}
						</ImageListItem>
					);
				})}
			</ImageList>
		</Box>
	);
};

export const SimpleThumbnailList = ({images}: Props) => {
	return <ImageList
		variant="standard"
		cols={4}
		rowHeight={100}
		sx={{m:0, gap: 1}}
	>
		{images.map((image) => {
			return (
				<ImageListItem key={image.imgUrl} sx={{borderRadius: 2, overflow: 'hidden', boxShadow: 3}}>
					<img
						src={image.imgUrl}
						alt={image.title}
						loading="lazy"
						style={{overflow:'hidden'}}
					/>
				</ImageListItem>
			);})}
	</ImageList>;
};